import { supabase } from "@/integrations/supabase/client";
import type { ClassItem, ClassSession, SupplyList } from "@/data/classes";
import { coerceBillingUnit, publicPriceLabel } from "@/lib/classPricing";

// Legacy bundled assets kept as an image fallback for classes migrated during
// Phase D. Admin can replace via the Media Manager, which sets thumbnail_url.
import beginnersBasketball from "@/assets/beginners-basketball.jpg";
import legoRobotics from "@/assets/lego-robotics.jpg";
import beginnersMechanics from "@/assets/beginners-mechanics.jpg";
import coloradoRiver from "@/assets/colorado-river.jpg";
import chessClub from "@/assets/chess-club.jpg";
import mommyMeCrochet from "@/assets/mommy-me-crochet.jpg";
import chickensEggs from "@/assets/chickens-eggs.jpg";
import hoovedAndHens from "@/assets/hooved-and-hens.jpg";
import mommyMeBaking2 from "@/assets/mommy-me-baking-2.jpg";
import peClass from "@/assets/pe-class.jpg";
import photographyClass from "@/assets/photography-class.jpg";
import beginnersWoodworking from "@/assets/beginners-woodworking.jpg";
import beginnersMetalWorking from "@/assets/beginners-metal-working.jpg";
import beginnersMetalworking from "@/assets/beginners-metalworking.jpg";
import mommyMeSelfDefense from "@/assets/mommy-me-self-defense.jpg.asset.json";
import lineDancing from "@/assets/line-dancing.jpg.asset.json";
import introToChoir from "@/assets/intro-to-choir.jpg.asset.json";
import improvForBeginners from "@/assets/improv-for-beginners.jpg.asset.json";
import beginnersVolleyball from "@/assets/beginners-volleyball.jpg.asset.json";

const POKEMON_CLUB_IMAGE =
  "https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?w=800&q=80";

const LEGACY_IMAGE_BY_SLUG: Record<string, string> = {
  // Batch 1
  "beginners-basketball": beginnersBasketball,
  "lego-robotics": legoRobotics,
  "beginners-mechanics": beginnersMechanics,
  "biology-lake-mead": coloradoRiver,
  // Batch 2
  "chess-club": chessClub,
  "mommy-me-crochet": mommyMeCrochet,
  "all-about-farming": chickensEggs,
  "hooved-and-hens": hoovedAndHens,
  "pokemon-club": POKEMON_CLUB_IMAGE,
  // Batch 3 — final legacy migration
  "mommy-me-baking-2": mommyMeBaking2,
  "pe-class": peClass,
  "photography-for-teens": photographyClass,
  "beginners-woodworking": beginnersWoodworking,
  "beginners-metal-working": beginnersMetalWorking,
  "beginners-metalworking": beginnersMetalworking,
  "mommy-me-self-defense": mommyMeSelfDefense.url,
  "mommy-me-line-dancing": lineDancing.url,
  "intro-to-choir": introToChoir.url,
  "improv-for-beginners": improvForBeginners.url,
  "beginners-volleyball": beginnersVolleyball.url,
};


/**
 * Shared class image resolver. Used by both the public catalog and the admin
 * Operations Board so they never disagree on what image renders for a class.
 * Resolution order: DB thumbnail → migrated legacy asset by stable slug → null.
 * The caller decides what placeholder (if any) to render when this returns null.
 */
export function resolveClassImage(
  slug: string | null | undefined,
  thumbnailUrl: string | null | undefined,
): string | null {
  if (thumbnailUrl) return thumbnailUrl;
  if (slug && LEGACY_IMAGE_BY_SLUG[slug]) return LEGACY_IMAGE_BY_SLUG[slug];
  return null;
}


export type DbClassRow = {
  id: string;
  slug: string | null;
  title: string;
  description: string;
  short_description: string | null;
  category: string | null;
  ages: string | null;
  capacity: number;
  price_per_child: number | null;
  price_display: string | null;
  billing_unit: string | null;
  class_length: string | null;
  preferred_days: string | null;
  preferred_time: string | null;
  schedule: string | null;
  location: string | null;
  room: string | null;
  start_date: string | null;
  duration: string | null;
  weekly_breakdown: unknown;
  sessions: unknown;
  supplies: unknown;
  facility_requirements: string | null;
  thumbnail_url: string | null;
  thumbnail_source: string | null;
  status: "draft" | "coming_soon" | "published" | "archived";
  accepts_registrations: boolean;
  /** @deprecated migration-only compat; runtime calendar authority is class_registry.calendar_visible */
  hidden_from_calendar: boolean;
  hidden: boolean;
  instructor_id: string | null;
  external_signup_url: string | null;
  management_type?: "silver_sage" | "external_provider" | null;
  provider_name?: string | null;
  provider_contact_email?: string | null;
  provider_contact_phone?: string | null;
  provider_public_contact_text?: string | null;
  proposal_id: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
};

/**
 * Every classes column that non-admin (anon/authenticated) roles may read.
 * provider_contact_email / provider_contact_phone are internal-only and are
 * revoked at the database level — never add them here.
 */
export const CLASS_SAFE_COLUMNS =
  "id, slug, title, description, short_description, category, ages, min_enrollment, capacity, price_per_child, price_display, billing_unit, class_length, preferred_days, preferred_time, schedule, start_date, duration, weekly_breakdown, sessions, supplies, facility_requirements, location, room, thumbnail_url, thumbnail_source, status, accepts_registrations, hidden_from_calendar, hidden, instructor_id, proposal_id, created_at, updated_at, published_at, external_signup_url, management_type, provider_name, provider_public_contact_text, price_display_omits_unit";



const PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'>
      <defs>
        <linearGradient id='bg' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0%' stop-color='#f5f0e6'/>
          <stop offset='100%' stop-color='#dbe4d2'/>
        </linearGradient>
      </defs>
      <rect width='400' height='300' fill='url(#bg)'/>
      <g transform='translate(200 130)' fill='none' stroke='#5f7a4b' stroke-width='6' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M0 40 C 0 0, -30 -20, -55 -20 C -50 10, -25 40, 0 40 Z' fill='#8fa878' fill-opacity='0.35'/>
        <path d='M0 40 C 0 0, 30 -20, 55 -20 C 50 10, 25 40, 0 40 Z' fill='#8fa878' fill-opacity='0.35'/>
        <path d='M0 40 V -30' />
      </g>
      <text x='50%' y='230' text-anchor='middle' font-family='Georgia, serif' font-size='20' fill='#5f7a4b' font-style='italic'>Silver Sage</text>
      <text x='50%' y='255' text-anchor='middle' font-family='sans-serif' font-size='12' letter-spacing='2' fill='#7a8b6b'>HOMESCHOOL HUB</text>
    </svg>`,
  );

function asStringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === "string");
  return out.length ? out : undefined;
}

function asSessions(v: unknown): ClassSession[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
    .map((x) => ({
      label: String(x.label ?? ""),
      dates: String(x.dates ?? ""),
      location: typeof x.location === "string" ? x.location : undefined,
      room: typeof x.room === "string" ? x.room : undefined,
      price: typeof x.price === "number" ? x.price : undefined,
      full: typeof x.full === "boolean" ? x.full : undefined,
      closed: typeof x.closed === "boolean" ? x.closed : undefined,
    }))
    .filter((s) => s.label);
  return out.length ? out : undefined;
}

function asSupplyList(v: unknown): SupplyList | undefined {
  if (!v || typeof v !== "object") return undefined;
  const obj = v as Record<string, unknown>;
  const mapItems = (arr: unknown) =>
    Array.isArray(arr)
      ? arr
          .filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
          .map((x) => ({
            name: String(x.name ?? ""),
            url: String(x.url ?? ""),
            required: typeof x.required === "boolean" ? x.required : undefined,
          }))
          .filter((i) => i.name)
      : undefined;
  const required = mapItems(obj.required);
  const recommended = mapItems(obj.recommended);
  const note = typeof obj.note === "string" ? obj.note : undefined;
  if (!required?.length && !recommended?.length && !note) return undefined;
  return { required, recommended, note };
}

export function dbClassToClassItem(row: DbClassRow): ClassItem {
  const slug = row.slug ?? row.id;
  const image = resolveClassImage(slug, row.thumbnail_url) ?? PLACEHOLDER;
  const billingUnit = coerceBillingUnit(row.billing_unit);
  const isExternal = (row.management_type ?? "silver_sage") === "external_provider";
  const hasInstructor = !!row.instructor_id;
  const durationWeeks = parseDurationWeeks(row.duration ?? row.class_length);
  const priceLabel = publicPriceLabel({
    pricePerChild: row.price_per_child,
    billingUnit,
    priceDisplay: row.price_display,
    externalPayment: isExternal,
    hasInstructor: isExternal ? true : hasInstructor,
  });

  return {
    // Keep the natural slug in the id so registry lookups (`slugOf`) resolve
    // to the same key used by static entries.
    id: `db:${slug}`,
    title: row.title,
    description: row.description,
    shortDescription: row.short_description ?? undefined,
    ages: row.ages ?? "",
    schedule: row.schedule ?? "",
    duration: row.duration ?? row.class_length ?? "",
    price: priceLabel,
    unitPrice: row.price_per_child ?? 0,
    startDate: row.start_date ?? "TBA",
    status: row.status === "published" && row.accepts_registrations ? "open" : "coming-soon",
    image,
    category: row.category ?? "New",
    location: row.location ?? undefined,
    room: row.room ?? undefined,
    sessions: asSessions(row.sessions),
    weeklyBreakdown: asStringArray(row.weekly_breakdown),
    supplyList: asSupplyList(row.supplies),
    externalSignupUrl: row.external_signup_url ?? undefined,
    billingUnit,
    // Calendar visibility is owned by class_registry.calendar_visible (see CalendarPage).
    // Intentionally not propagated from classes.hidden_from_calendar (migration-only compat).
    hidden: row.hidden,
    managementType: (row.management_type ?? "silver_sage") as "silver_sage" | "external_provider",
    providerName: row.provider_name ?? undefined,
    providerContactEmail: row.provider_contact_email ?? undefined,
    providerContactPhone: row.provider_contact_phone ?? undefined,
    providerPublicContactText: row.provider_public_contact_text ?? undefined,
    dbId: row.id,
    capacity: row.capacity ?? null,
    hasInstructor,
    sessionLengthWeeks: durationWeeks ?? 1,
  };
}

/** Infer session bundle length in weeks from free-form duration text. */
function parseDurationWeeks(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = s.match(/(\d+)\s*week/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export async function fetchPublicDbClasses(): Promise<ClassItem[]> {
  const { data, error } = await (supabase as any)
    .from("classes")
    .select(CLASS_SAFE_COLUMNS)
    .in("status", ["published", "coming_soon"])
    .eq("hidden", false)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("fetch db classes", error);
    return [];
  }
  return (data as DbClassRow[]).map(dbClassToClassItem);
}
